import type {InterfaceModel,TenantInvitationAdminApi} from './model';

export type TenantResolveRequest={
  legalName:string;
  reportingCurrency:string;
  entity:{name:string;kind:'corporation'|'llc'|'partnership'|'sole_proprietorship'|'other';fiscalYearEndMonth:number};
  reconstructionPeriod:{start:string;end:string};
};

export type TenantIdentity={
  id:string;legalName:string;reportingCurrency:string;
  reconstructionPeriod:{start:string;end:string};
  entities:{id:string;name:string;kind:TenantResolveRequest['entity']['kind'];fiscalYearEndMonth:number}[];
  membership:{subjectId:string;roles:string[];permissions:string[]};
};

export type TenantInvitationRequest=TenantResolveRequest&{
  owner:{name:string;email:string;role:'owner'};
};

export type InvitationState='delivery-requested'|'delivery-accepted'|'delivery-failed'|'pending-acceptance'|'accepted'|'expired'|'revoked'|'superseded';
export type ProviderReceipt={state:'not-requested'|'accepted'|'delivered'|'refused';reference:string|null;observedAt:string|null};
export type TenantInvitation={
  id:string;tenant:TenantIdentity;invitee:{name:string;email:string;role:'owner'};
  state:InvitationState;attempt:number;updatedAt:string;retryable:boolean;
  receipts:{auth0:ProviderReceipt;postmark:ProviderReceipt};credentialPayloadPresent:false;
};

export type ApiProblem={
  type:string;title:string;status:number;code:'authentication_required'|'permission_denied'|'tenant_boundary_violation'|'not_found'|
    'validation_failed'|'version_conflict'|'provider_action_required'|'lifecycle_conflict'|'rate_limited'|'temporarily_unavailable';
  detail:string;retryable:boolean;retryAfterSeconds:number|null;requiredAction:string|null;requestId:string;
};

export type TenantInvitationResult={ok:true;invitation:TenantInvitation}|{ok:false;problem:ApiProblem};

export type TenantInvitationSummary={
  id:string;tenant:{id:string;legalName:string};invitee:{name:string;email:string;role:'owner'};
  state:InvitationState;updatedAt:string;retryable:boolean;
  graph:{identity:string;boundary:'tallyup://boundary/application/tallyup/tenant-invitation/v1'};credentialPayloadPresent:false;
};
export type TenantInvitationCollection={items:TenantInvitationSummary[];count:number;graphBoundary:'tallyup://boundary/application/tallyup/tenant-invitation/v1';credentialPayloadPresent:false};
export type TenantInvitationAdminResult={ok:true;invitation:TenantInvitationSummary}|{ok:false;problem:ApiProblem};
export type TenantInvitationListResult={ok:true;collection:TenantInvitationCollection}|{ok:false;problem:ApiProblem};

const tenantIdPattern=/^ten_[A-Za-z0-9_-]{6,64}$/;
const invitationIdPattern=/^inv_[A-Za-z0-9_-]{6,64}$/;
const datePattern=/^\d{4}-\d{2}-\d{2}$/;
const emailPattern=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateTenantInvitationRequest(request:TenantInvitationRequest){
  if(!request.legalName.trim()||!request.entity.name.trim())throw new Error('Legal and entity names are required.');
  if(!request.owner.name.trim()||!emailPattern.test(request.owner.email))throw new Error('A valid first owner name and email are required.');
  if(request.owner.role!=='owner')throw new Error('The first tenant member must be an owner.');
  if(!/^[A-Z]{3}$/.test(request.reportingCurrency))throw new Error('Reporting currency must be a three-letter code.');
  if(!Number.isInteger(request.entity.fiscalYearEndMonth)||request.entity.fiscalYearEndMonth<1||request.entity.fiscalYearEndMonth>12)
    throw new Error('Fiscal year-end month must be between 1 and 12.');
  if(!datePattern.test(request.reconstructionPeriod.start)||!datePattern.test(request.reconstructionPeriod.end)
    ||request.reconstructionPeriod.start>request.reconstructionPeriod.end)throw new Error('Reconstruction period is invalid.');
}

function validateTenantIdentity(value:unknown):TenantIdentity{
  const tenant=value as Partial<TenantIdentity>;
  if(!tenantIdPattern.test(tenant.id||'')||!tenant.legalName||!Array.isArray(tenant.entities)||tenant.entities.length===0
    ||!tenant.membership||!Array.isArray(tenant.membership.roles)||!Array.isArray(tenant.membership.permissions))
    throw new Error('Tenant identity in invitation response was refused.');
  return tenant as TenantIdentity;
}

function validateReceipt(value:unknown):ProviderReceipt{
  const receipt=value as Partial<ProviderReceipt>;
  if(!receipt||!['not-requested','accepted','delivered','refused'].includes(receipt.state||'')
    ||!(typeof receipt.reference==='string'||receipt.reference===null)
    ||!(typeof receipt.observedAt==='string'||receipt.observedAt===null))throw new Error('Provider receipt was refused.');
  return receipt as ProviderReceipt;
}

function validateInvitation(value:unknown):TenantInvitation{
  const invitation=value as Partial<TenantInvitation>;
  if(!invitationIdPattern.test(invitation.id||'')||!invitation.invitee||invitation.invitee.role!=='owner'
    ||!emailPattern.test(invitation.invitee.email||'')||!invitation.receipts
    ||invitation.credentialPayloadPresent!==false||!Number.isInteger(invitation.attempt)||!invitation.updatedAt
    ||!['delivery-requested','delivery-accepted','delivery-failed','pending-acceptance','accepted','expired','revoked','superseded'].includes(invitation.state||''))
    throw new Error('Tenant invitation response was refused.');
  validateTenantIdentity(invitation.tenant);
  validateReceipt(invitation.receipts.auth0);
  validateReceipt(invitation.receipts.postmark);
  return invitation as TenantInvitation;
}

function validateInvitationSummary(value:unknown):TenantInvitationSummary{
  const invitation=value as Partial<TenantInvitationSummary>;
  if(!invitationIdPattern.test(invitation.id||'')||!invitation.tenant||!tenantIdPattern.test(invitation.tenant.id||'')
    ||!invitation.tenant.legalName||!invitation.invitee||invitation.invitee.role!=='owner'||!emailPattern.test(invitation.invitee.email||'')
    ||!invitation.updatedAt||invitation.credentialPayloadPresent!==false||!invitation.graph
    ||invitation.graph.boundary!=='tallyup://boundary/application/tallyup/tenant-invitation/v1'
    ||typeof invitation.graph.identity!=='string'||!invitation.graph.identity.startsWith('tallyup://graph/')
    ||!['delivery-requested','delivery-accepted','delivery-failed','pending-acceptance','accepted','expired','revoked','superseded'].includes(invitation.state||''))
    throw new Error('Tenant invitation summary was refused.');
  return invitation as TenantInvitationSummary;
}

function validateInvitationCollection(value:unknown):TenantInvitationCollection{
  const collection=value as Partial<TenantInvitationCollection>;
  if(!Array.isArray(collection.items)||collection.items.length>100||collection.count!==collection.items.length
    ||collection.graphBoundary!=='tallyup://boundary/application/tallyup/tenant-invitation/v1'||collection.credentialPayloadPresent!==false)
    throw new Error('Tenant invitation collection was refused.');
  collection.items.forEach(validateInvitationSummary);
  return collection as TenantInvitationCollection;
}

function validateProblem(value:unknown,status:number):ApiProblem{
  const problem=value as Partial<ApiProblem>;
  if(problem.status!==status||typeof problem.code!=='string'||typeof problem.detail!=='string'||typeof problem.retryable!=='boolean'
    ||typeof problem.requestId!=='string')throw new Error('Tenant invitation refusal response is invalid.');
  return problem as ApiProblem;
}

export function tenantInvitationPath(model:InterfaceModel){
  return `${model.activationApi.mountPath}${model.activationApi.pathTemplate}`;
}

export async function inviteFirstTenantOwner(
  model:InterfaceModel,token:string,idempotencyKey:string,request:TenantInvitationRequest,
  transport:typeof fetch=fetch
):Promise<TenantInvitationResult>{
  validateTenantInvitationRequest(request);
  if(!model.activationApi.clientEffectAdmitted)return {ok:false,problem:{
    type:'about:blank',title:'Tenant invitation is not admitted',status:503,
    code:'temporarily_unavailable',detail:'This client distribution has not admitted the invitation effect. No request was sent and no tenant or invitation was created.',
    retryable:false,retryAfterSeconds:null,requiredAction:null,requestId:'client-contract-refusal'
  }};
  if(idempotencyKey.length<16||idempotencyKey.length>128)throw new Error('Idempotency key must contain 16 to 128 characters.');
  const response=await transport(tenantInvitationPath(model),{method:'POST',headers:{accept:'application/json','content-type':'application/json',authorization:`Bearer ${token}`,[model.activationApi.idempotencyHeader]:idempotencyKey},body:JSON.stringify(request),cache:'no-store'});
  const body:unknown=await response.json();
  return response.ok?{ok:true,invitation:validateInvitation(body)}:{ok:false,problem:validateProblem(body,response.status)};
}

function invitationApi(model:InterfaceModel,identifier:TenantInvitationAdminApi['identifier']){
  const selected=model.invitationApis.filter(api=>api.identifier===identifier);
  if(selected.length!==1)throw new Error(`Invitation capability ${identifier} is unavailable.`);
  return selected[0];
}

function invitationAdminPath(api:TenantInvitationAdminApi,invitationId?:string){
  const path=invitationId===undefined?api.pathTemplate:api.pathTemplate.replace('{invitationId}',encodeURIComponent(invitationId));
  if(path.includes('{'))throw new Error('Invitation API path is incomplete.');
  return `${api.mountPath}${path}`;
}

export async function listTenantInvitations(model:InterfaceModel,token:string,transport:typeof fetch=fetch):Promise<TenantInvitationListResult>{
  const api=invitationApi(model,'listTenantInvitations');
  const response=await transport(invitationAdminPath(api),{method:'GET',headers:{accept:'application/json',authorization:`Bearer ${token}`},cache:'no-store'});
  const body:unknown=await response.json();
  return response.ok?{ok:true,collection:validateInvitationCollection(body)}:{ok:false,problem:validateProblem(body,response.status)};
}

export async function readTenantInvitation(model:InterfaceModel,token:string,invitationId:string,transport:typeof fetch=fetch):Promise<TenantInvitationAdminResult>{
  if(!invitationIdPattern.test(invitationId))throw new Error('Invitation identity is invalid.');
  const api=invitationApi(model,'readTenantInvitation');
  const response=await transport(invitationAdminPath(api,invitationId),{method:'GET',headers:{accept:'application/json',authorization:`Bearer ${token}`},cache:'no-store'});
  const body:unknown=await response.json();
  return response.ok?{ok:true,invitation:validateInvitationSummary(body)}:{ok:false,problem:validateProblem(body,response.status)};
}

export async function transitionTenantInvitation(model:InterfaceModel,token:string,invitationId:string,action:'revoke'|'supersede',idempotencyKey:string,transport:typeof fetch=fetch):Promise<TenantInvitationAdminResult>{
  if(!invitationIdPattern.test(invitationId))throw new Error('Invitation identity is invalid.');
  if(idempotencyKey.length<16||idempotencyKey.length>128)throw new Error('Idempotency key must contain 16 to 128 characters.');
  const api=invitationApi(model,action==='revoke'?'revokeTenantInvitation':'supersedeTenantInvitation');
  if(!api.clientEffectAdmitted)return {ok:false,problem:{type:'about:blank',title:'Invitation transition is not admitted',status:503,code:'temporarily_unavailable',detail:'This client distribution has not admitted the requested invitation transition.',retryable:false,retryAfterSeconds:null,requiredAction:null,requestId:'client-contract-refusal'}};
  const response=await transport(invitationAdminPath(api,invitationId),{method:'POST',headers:{accept:'application/json',authorization:`Bearer ${token}`,[api.idempotencyHeader!]:idempotencyKey},cache:'no-store'});
  const body:unknown=await response.json();
  return response.ok?{ok:true,invitation:validateInvitationSummary(body)}:{ok:false,problem:validateProblem(body,response.status)};
}
